import config from "appConfig";

const theory = `
<h3 class=sectionHead>Introduction</h3>
<p>Impulse responses, suitable for signal processing and particularly for auralization, must have a sampling rate appropriate for the audio frequency range, typically 44.1 kHz. Thus, all algorithms which deliver impulse response in high temporal resolution are applicable. This applies to image source algorithms or hybrid models. Other algorithms such as radiosity or stochastic ray tracing require post processing in order to generate a synthesized result in high temporal resolution, as explained below.<br class=newline>
<p>Concerning the amplitude, the sampling resolution must be taken into account, too. This is usually not a big issue but in cases of very small filter coefficients the maximum scale corresponding to an analog/digital (A/D) conversion must be matched with the maximum-to-minimum coefficients in order to use the given range of digital numbers in a well-balanced way. For 16 bit resolution, for example, this corresponds to 96 dB dynamic range with only negligible quantization noise.<br class=newline>
<p>Finally, the impulse responses in proper amplitude and temporal resolution are the coefficients of an finite impulse response (FIR) filter which can be used for convolution with source signals.
<h3 class=sectionHead>Impulse responses with distinct peaks</h3>
<p>
   Constructing a room impulse response (RIR) from audible image sources (ISs) is rather simple. Each reflection initially shows up as a Dirac delta function, which is delayed by the travel time and attenuated by both the wall absorption on each reflection and the absorption by air according to the travel distance, shown in <a href=# data-img-alt="Figure 1. Construction of a RIR (time domain)." data-img-src=${config.ASSETS_URL}/03/17/theory/figure-1.svg>Figure 1</a>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Construction of a RIR (time domain)." src=${config.ASSETS_URL}/03/17/theory/figure-1.svg><figcaption class=caption>Figure 1. Construction of a RIR (time domain).</figcaption></figure>
<p>Each reflection has a filter in the spectral domain as given in Eq.<nobr> 1</nobr>.
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <munder class=mml-underline accentunder=false>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mo accent=true>̲</mo>
                  </munder>
               </mrow>
               <mrow>
                  <mi>j</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jωt</mi>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <msub>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <mi>H</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>air</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>t</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <munder class=mml-underline accentunder=false>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mo accent=true>̲</mo>
                  </munder>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>Source</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <msub>
               <mrow>
                  <munder class=mml-underline accentunder=false>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mo accent=true>̲</mo>
                  </munder>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>Receiver</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>𝜗</mi>
            <mi>φ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∏</mo>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                  </msub>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <munder class=mml-underline accentunder=false>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mo accent=true>̲</mo>
                  </munder>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
         </mrow>
      </msub>
   </math>
   denotes the spectrum of the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>j</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>th</mi>
         </mrow>
      </msup>
   </math>
   reflection path, i.e. an audible IS,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
         </mrow>
      </msub>
   </math>
   is the delay time with phase shift
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>jωtj</mi>
   </math>
   ,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ctj</mi>
         </mrow>
      </mfrac>
   </math>
   the distance law for spherical waves,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Source</mi>
         </mrow>
      </msub>
   </math>
   the directional pattern of the source,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Receiver</mi>
         </mrow>
      </msub>
   </math>
   the directional pattern of the receiver, where the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ϕ</mi>
   </math>
   are defining the directional angle,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>air</mi>
         </mrow>
      </msub>
   </math>
   is air attenuation and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   the reflection factors of the walls that were hit by the reflection [1].<br class=newline>
<p>All contributions from IS are summed up which finally leads to the room transfer function, and to its equivalent in time domain: the impulse response [3].
<h3 class=sectionHead>Impulse responses with envelopes in time slots and frequency bands</h3>
<p>Algorithms may do not deliver high temporal resolution but only impulse response envelopes in certain temporal blocks (“time slots”). Examples for this methods are ray tracing or radiosity. In this case the impulse response filter must be synthesized with reasonable assumptions. As long as the time slots are smaller than the time scale of temporal masking, the result is conserved as perceptually sufficient. With knowledge about the internal physical fine structure such as the actual reflection density it is straightforward to generate an arbitrary impulse sequence within each time slot.<br class=newline>
<p>
   To generate a binaural impulse response a few methods are available, one option is to make a spherical detector with discrete Directivity Groups. Then for each directivity group, a temporal-spectral histogram will be generated as shown in <a href=# data-img-alt="Figure 2. Special detector for binaural impulse response synthesis." data-img-src=${config.ASSETS_URL}/03/17/theory/figure-2.svg>Figure 2</a>. The impulse response in high resolution is synthesizes from Poisson sequences, for example, which are spectrally shaped according to the energies in the frequency bands. For more details see [2].
   <figure class=figure>
<p><img alt="Figure 2. Special detector for binaural impulse response synthesis." src=${config.ASSETS_URL}/03/17/theory/figure-2.svg><figcaption class=caption>Figure 2. Special detector for binaural impulse response synthesis.</figcaption></figure>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>A/D
  <dd>Analog/Digital
  <dt>FIR
  <dd>Finite Impulse Response
  <dt>ISs
  <dd>Image Sources
  <dt>RIR
  <dd>Room Impulse Response
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>Jont B Allen and David A Berkley. “Image method for efficiently simulating small-room acoustics”. In: J. Acoust. Soc. Am. 65.4 (Apr. 1979), pp. 943–950. doi: <a href="https://doi.org/https://doi.org/10.1121/1.382599" target=_blank>https://doi.org/10.1121/1.382599</a>.
   <dt>[2]
   <dd>S ̈onke Pelzer et al. “Integrating real-time room acoustics simulation into aCAD modeling software to enhance the architectural design process”. In:Building Acoustics 4.2 (Apr. 2014), pp. 65–74. doi: <a href="https://doi.org/10.3390/buildings4020113" target=_blank>10.3390/buildings4020113</a>.
   <dt>[3]
   <dd>Michael Vorl ̈ander. Auralization. Fundamentals of acoustics, modelling, simulation, algorithms and acoustic virtual reality. 2nd. Berlin, Heidelberg, Germany: Springer Nature Switzerland AG 2020, 383 pages October 2020.
</dl>
`;
export default theory;
